/**
 * Safe localStorage wrapper
 * Handles cases where localStorage is not available (private browsing, security restrictions)
 */

class SafeStorage {
  isAvailable: boolean
  private memoryFallback: Map<string, string>

  constructor() {
    this.isAvailable = this.checkAvailability()
    this.memoryFallback = new Map()
  }

  private checkAvailability(): boolean {
    try {
      const testKey = '__storage_test__'
      window.localStorage.setItem(testKey, testKey)
      window.localStorage.removeItem(testKey)
      return true
    } catch {
      return false
    }
  }

  getItem(key: string): string | null {
    try {
      if (this.isAvailable) {
        return window.localStorage.getItem(key)
      }
      return this.memoryFallback.get(key) ?? null
    } catch {
      return this.memoryFallback.get(key) ?? null
    }
  }

  setItem(key: string, value: string): void {
    try {
      if (this.isAvailable) {
        window.localStorage.setItem(key, value)
      }
      this.memoryFallback.set(key, value)
    } catch {
      this.memoryFallback.set(key, value)
    }
  }

  removeItem(key: string): void {
    try {
      if (this.isAvailable) {
        window.localStorage.removeItem(key)
      }
      this.memoryFallback.delete(key)
    } catch {
      this.memoryFallback.delete(key)
    }
  }

  clear(): void {
    try {
      if (this.isAvailable) {
        window.localStorage.clear()
      }
      this.memoryFallback.clear()
    } catch {
      this.memoryFallback.clear()
    }
  }

  getJSON<T = unknown>(key: string, defaultValue: T | null = null): T | null {
    try {
      const value = this.getItem(key)
      if (value === null) return defaultValue
      return JSON.parse(value) as T
    } catch {
      return defaultValue
    }
  }

  setJSON(key: string, value: unknown): void {
    try {
      this.setItem(key, JSON.stringify(value))
    } catch {
      // silently fail
    }
  }
}

export const storage = new SafeStorage()
